import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { cn } from './ui';

/**
 * The bell in the staff top bar (server: notifications/staff.ts). What needs this person's attention: a shop that
 * reported "I have paid" (owner, accountant); their report confirmed or rejected, a new charge or advertising share
 * on their ledger (shop admin). The count refreshes every minute, when the tab comes back, and straight away when
 * the live stream brings a related change (lib/live.ts refreshes ['staff-notifications']). Opening one marks it
 * read and goes to its page; nothing is shown when the server has no notifications for this login.
 */

export interface StaffNotification {
  id: number;
  outlet_id: string | null;
  outlet_name: string | null;
  kind: string;
  title: string;
  body: string | null;
  url: string | null;
  created_at: string;
  read_at: string | null;
}

export const NOTIFICATIONS_KEY = ['staff-notifications'] as const;

export function NotificationBell({ className }: { className?: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => api<{ items: StaffNotification[]; unread: number }>('/staff-notifications?limit=30'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // A failed count is not worth an error on every page: the bell just shows no number.
    retry: 1,
  });
  const markRead = useMutation({
    mutationFn: (id: number) => api(`/staff-notifications/${id}/read`, { body: {} }),
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
  const markAll = useMutation({
    mutationFn: () => api('/staff-notifications/read-all', { body: {} }),
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
  const unread = q.data?.unread ?? 0;
  const items = q.data?.items ?? [];
  const openOne = (n: StaffNotification) => {
    if (!n.read_at) markRead.mutate(n.id);
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        data-testid="notification-bell"
        className="relative flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 sm:h-9 sm:w-9"
      >
        <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute right-1 top-1 min-w-[1.1rem] rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-white sm:right-0 sm:top-0">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-40 cursor-default" aria-label="Close notifications" onClick={() => setOpen(false)} />
          {/* The top bar's blur keeps the backdrop above inside the bar; this one covers the page below it. */}
          {createPortal(<button type="button" tabIndex={-1} aria-hidden className="fixed inset-0 z-20 cursor-default" onClick={() => setOpen(false)} />, document.body)}
          <div
            role="dialog"
            aria-label="Notifications"
            className="fixed inset-x-2 top-14 z-50 max-h-[70vh] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-1 sm:w-96"
          >
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
              <p className="text-sm font-semibold text-slate-900">Notifications</p>
              {unread > 0 && (
                <button type="button" className="min-h-11 text-xs font-medium text-brand-700 hover:underline lg:min-h-0" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[calc(70vh-3rem)] overflow-y-auto">
              {q.isError ? (
                <p className="px-3 py-4 text-sm text-slate-500">Notifications could not be loaded. They will appear when the connection is back.</p>
              ) : items.length === 0 ? (
                <p className="px-3 py-4 text-sm text-slate-500">{q.isLoading ? 'Loading…' : 'Nothing new. Payments reported, confirmed or charged to a shop show here.'}</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => openOne(n)}
                        data-testid="notification-item"
                        className={cn('flex w-full gap-2 px-3 py-2.5 text-left hover:bg-slate-50', !n.read_at && 'bg-brand-50/60')}
                      >
                        <span aria-hidden className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.read_at ? 'bg-transparent' : 'bg-brand-600')} />
                        <span className="min-w-0">
                          <span className={cn('block text-sm text-slate-900', !n.read_at && 'font-semibold')}>{n.title}</span>
                          {n.body && <span className="mt-0.5 block text-xs text-slate-600">{n.body}</span>}
                          <span className="mt-0.5 block text-xs text-slate-400">
                            {n.outlet_name ? `${n.outlet_name} · ` : ''}
                            {dateTime(n.created_at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
